(() => {
  'use strict';

  const operationProperty = '_Designer operation ID';
  const storagePrefix = 'mel-bracelet-designer-cart-operation-v1:';
  const asString = (value) => String(value ?? '').trim();
  const toCents = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? Math.round(amount * 100) : null;
  };
  const makeError = (code, message) => Object.assign(new Error(message || code), { code });
  const makeUnknown = (record, cartUrl, message) => Object.assign(
    makeError('CART_OUTCOME_UNKNOWN', message || 'We could not confirm the cart update. No second add request was sent.'),
    { operationId: asString(record?.operationId), cartUrl }
  );
  const operationStorageKey = (designId) => storagePrefix + encodeURIComponent(asString(designId));
  const itemDesignId = (item) => asString(item?.properties?.['Design ID']);
  const itemOperationId = (item) => asString(item?.properties?.[operationProperty]);
  const itemVariantId = (item) => asString(item?.variant_id ?? item?.id);

  const createCartClient = (scope) => {
    const timeoutRequest = async (url, options, timeoutMs, fetcher) => {
      const controller = typeof scope.AbortController === 'function' ? new scope.AbortController() : null;
      let timeoutId;
      let rejectTimeout;
      const timeout = new Promise((resolve, reject) => {
        rejectTimeout = reject;
        timeoutId = scope.setTimeout(() => {
          controller?.abort();
          reject(makeError('CART_TIMEOUT', 'Cart request timed out.'));
        }, timeoutMs);
      });
      try {
        const request = Promise.resolve(fetcher(url, { ...options, ...(controller ? { signal: controller.signal } : {}) }));
        return await Promise.race([request, timeout]);
      } finally {
        scope.clearTimeout(timeoutId);
        rejectTimeout = null;
      }
    };

    const readCart = async (cartUrl, timeoutMs, fetcher) => {
      const response = await timeoutRequest(cartUrl, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store'
      }, timeoutMs, fetcher);
      const cart = await response.json().catch(() => null);
      if (!response.ok || !Array.isArray(cart?.items)) {
        throw makeError('CART_READ', 'We could not verify the current cart.');
      }
      return cart;
    };

    const matchingItems = (cart, variantId, designId) => cart.items.filter((item) =>
      itemVariantId(item) === asString(variantId) && itemDesignId(item) === asString(designId)
    );
    const quantityOf = (items) => items.reduce((quantity, item) => quantity + (Number(item.quantity) || 0), 0);
    const matchingOperationItems = (items, operationId) => items.filter((item) => itemOperationId(item) === asString(operationId));
    const verifyAddedLine = (items, operationId, expectedAmount) => {
      const operationItems = matchingOperationItems(items, operationId);
      const operationQuantity = quantityOf(operationItems);
      if (operationItems.length !== 1 || operationQuantity !== 1) {
        throw makeError('CART_DELTA_MISMATCH', 'The cart did not contain exactly one item for this add operation.');
      }
      const expectedCents = toCents(expectedAmount);
      if (expectedCents !== null) {
        const lineCents = Number(operationItems[0].original_price ?? operationItems[0].price);
        if (!Number.isFinite(lineCents) || lineCents !== expectedCents) {
          throw makeError('CART_PRICE_MISMATCH', 'The cart price did not match the server-verified design price.');
        }
      }
      return operationItems[0];
    };

    const inspectOperation = async (record, cartUrl, timeoutMs, fetcher) => {
      const cart = await readCart(cartUrl, timeoutMs, fetcher);
      const expectedCurrency = asString(record.expectedCurrency).toUpperCase();
      const actualCurrency = asString(cart.currency).toUpperCase();
      if (expectedCurrency && !actualCurrency) {
        throw makeError('CART_CURRENCY_MISSING', 'We could not verify your cart currency.');
      }
      if (expectedCurrency && actualCurrency !== expectedCurrency) {
        throw makeError('CART_CURRENCY_MISMATCH', 'Your cart currency does not match the verified design currency.');
      }
      const items = matchingItems(cart, record.variantId, record.designId);
      const operationItems = matchingOperationItems(items, record.operationId);
      if (!operationItems.length) return { cart, items, found: false };
      const line = verifyAddedLine(items, record.operationId, record.expectedAmount);
      return { cart, items, line, found: true };
    };

    const reconcilePending = async (options = {}) => {
      const scopeWindow = options.window || scope;
      const routesRoot = options.routesRoot || scopeWindow.Shopify?.routes?.root || '/';
      const cartUrl = routesRoot + 'cart';
      const timeoutMs = Math.max(500, Number(options.timeoutMs) || 9000);
      const fetcher = options.fetch || scopeWindow.fetch?.bind(scopeWindow);
      let storage;
      try {
        storage = options.storage || scopeWindow.sessionStorage;
      } catch {
        throw makeError('CART_RECOVERY_UNAVAILABLE', 'This browser could not inspect the saved cart operation.');
      }
      if (!fetcher || !storage || !Number.isInteger(storage.length) || typeof storage.key !== 'function') {
        throw makeError('CART_RECOVERY_UNAVAILABLE', 'This browser could not inspect the saved cart operation.');
      }
      const entries = [];
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (!key?.startsWith(storagePrefix)) continue;
        let record;
        try {
          record = JSON.parse(storage.getItem(key) || 'null');
        } catch {
          throw makeError('CART_RECOVERY_UNAVAILABLE', 'The saved cart operation could not be read safely.');
        }
        if (record) entries.push({ key, record });
      }
      if (!entries.length) return null;
      const requestId = asString(options.requestId);
      const entry = entries.find(({ record }) => requestId && asString(record.requestId) === requestId);
      if (!entry || entries.length !== 1) {
        throw Object.assign(makeError('CART_RECOVERY_CONFLICT', 'An earlier bracelet cart update still needs verification.'), { cartUrl });
      }
      const { key, record } = entry;
      if (!record.variantId || !record.designId || !record.operationId ||
          record.expectedAmount == null || asString(record.expectedAmount) === '' ||
          toCents(record.expectedAmount) === null || !asString(record.expectedCurrency)) {
        throw Object.assign(makeError('CART_RECOVERY_UNAVAILABLE', 'The earlier cart update needs manual verification.'), { cartUrl });
      }
      let recovery;
      try {
        recovery = await inspectOperation(record, routesRoot + 'cart.js', timeoutMs, fetcher);
      } catch (error) {
        if (['CART_PRICE_MISMATCH', 'CART_DELTA_MISMATCH', 'CART_CURRENCY_MISMATCH', 'CART_CURRENCY_MISSING'].includes(error?.code)) throw error;
        throw makeUnknown(record, cartUrl, 'We could not confirm the earlier cart update. No second add request was sent.');
      }
      if (!recovery.found) {
        throw makeUnknown(record, cartUrl, 'We could not confirm the earlier cart update. No second add request was sent.');
      }
      storage.removeItem(key);
      return { ok: true, recovered: true, line: recovery.line, cartUrl };
    };

    const add = async (options = {}) => {
      const scopeWindow = options.window || scope;
      const routesRoot = options.routesRoot || scopeWindow.Shopify?.routes?.root || '/';
      const timeoutMs = Math.max(500, Number(options.timeoutMs) || 9000);
      const fetcher = options.fetch || scopeWindow.fetch?.bind(scopeWindow);
      let storage;
      try {
        storage = options.storage || scopeWindow.sessionStorage;
      } catch {
        throw makeError('CART_RECOVERY_UNAVAILABLE', 'This browser could not open a safe cart retry record.');
      }
      const variantId = asString(options.variantId);
      const designId = asString(options.designId || options.lineItemProperties?.['Design ID']);
      const expectedCurrency = asString(options.currencyCode).toUpperCase();
      const expectedAmount = options.amount;
      if (!fetcher) throw makeError('CART_FETCH_UNAVAILABLE', 'Cart requests are unavailable in this browser.');
      if (!variantId || !designId) throw makeError('CART_IDENTITY_MISSING', 'The design is missing its Shopify variant or design ID.');
      if (!storage) throw makeError('CART_RECOVERY_UNAVAILABLE', 'This browser could not save a safe cart retry record.');
      const storageKey = operationStorageKey(designId);
      const addUrl = routesRoot + 'cart/add.js';
      const cartUrl = routesRoot + 'cart.js';
      let pending = null;
      try {
        pending = JSON.parse(storage.getItem(storageKey) || 'null');
      } catch {
        throw makeError('CART_RECOVERY_UNAVAILABLE', 'The saved cart retry record could not be read safely.');
      }

      if (pending) {
        if (pending.variantId !== variantId || pending.designId !== designId) {
          throw makeError('CART_RECOVERY_CONFLICT', 'Another cart update for this design still needs verification.');
        }
        if (pending.requestId && options.requestId && pending.requestId !== options.requestId) {
          throw makeError('CART_RECOVERY_CONFLICT', 'Another cart update for this design still needs verification.');
        }
        let recovery;
        try {
          recovery = await inspectOperation({ ...pending, expectedAmount, expectedCurrency }, cartUrl, timeoutMs, fetcher);
        } catch (error) {
          if (error?.code === 'CART_PRICE_MISMATCH' || error?.code === 'CART_DELTA_MISMATCH' || error?.code === 'CART_CURRENCY_MISMATCH') {
            throw error;
          }
          throw makeUnknown(pending, routesRoot + 'cart', 'We could not confirm the earlier cart update. No second add request was sent.');
        }
        if (!recovery.found) {
          throw makeUnknown(pending, routesRoot + 'cart', 'We could not confirm the earlier cart update. No second add request was sent.');
        }
        storage.removeItem(storageKey);
        return { ok: true, recovered: true, line: recovery.line, cartUrl: routesRoot + 'cart' };
      }

      let before;
      try {
        before = await readCart(cartUrl, timeoutMs, fetcher);
      } catch {
        throw makeError('CART_READ', 'We could not read your cart, so nothing was added.');
      }
      const beforeCurrency = asString(before.currency).toUpperCase();
      if (expectedCurrency && !beforeCurrency) {
        throw makeError('CART_CURRENCY_MISSING', 'We could not verify your cart currency.');
      }
      if (expectedCurrency && beforeCurrency !== expectedCurrency) {
        throw makeError('CART_CURRENCY_MISMATCH', 'Your cart currency does not match the verified design currency.');
      }
      const beforeItems = matchingItems(before, variantId, designId);
      const operationId = asString(options.operationId) || scopeWindow.crypto?.randomUUID?.()
        || 'mel-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
      const record = {
        operationId,
        requestId: asString(options.requestId),
        variantId,
        designId,
        baselineQuantity: quantityOf(beforeItems),
        expectedAmount,
        expectedCurrency,
        createdAt: Date.now()
      };
      try {
        storage.setItem(storageKey, JSON.stringify(record));
      } catch {
        throw makeError('CART_RECOVERY_UNAVAILABLE', 'This browser could not save a safe cart retry record, so nothing was added.');
      }

      const properties = { ...(options.lineItemProperties || {}), 'Design ID': designId, [operationProperty]: operationId };
      const verifyOperation = async () => {
        try {
          return await inspectOperation(record, cartUrl, timeoutMs, fetcher);
        } catch (error) {
          if (['CART_PRICE_MISMATCH', 'CART_DELTA_MISMATCH', 'CART_CURRENCY_MISMATCH', 'CART_CURRENCY_MISSING'].includes(error?.code)) {
            throw error;
          }
          throw makeUnknown(record, routesRoot + 'cart', 'We could not verify whether the design reached your cart. No second add request was sent.');
        }
      };
      const added = (inspection, recovered) => {
        storage.removeItem(storageKey);
        return { ok: true, recovered, line: inspection.line, cartUrl: routesRoot + 'cart' };
      };
      for (let attempt = 0; attempt < 3; attempt += 1) {
        if (attempt) {
          await new Promise((resolve) => scopeWindow.setTimeout(resolve, 500 * attempt));
          const late = await verifyOperation();
          if (late.found) return added(late, true);
        }
        let postResponse = null;
        let postFailure = null;
        try {
          postResponse = await timeoutRequest(addUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties }] })
          }, timeoutMs, fetcher);
        } catch (error) {
          postFailure = error;
        }

        const after = await verifyOperation();
        if (after.found) return added(after, Boolean(postFailure || !postResponse?.ok || attempt));
        if (postResponse && !postResponse.ok && [400, 401, 403, 404, 422].includes(postResponse.status)) {
          if ([404, 422].includes(postResponse.status) && attempt < 2) continue;
          storage.removeItem(storageKey);
          const failure = await postResponse.json().catch(() => null);
          throw makeError('CART_ADD_REJECTED', failure?.description || failure?.message || 'The cart rejected this design (' + postResponse.status + ').');
        }
        throw makeUnknown(record, routesRoot + 'cart', postFailure?.message || 'We could not confirm the cart update. No second add request was sent.');
      }
    };

    return Object.freeze({ add, reconcilePending, operationProperty });
  };

  window.MelDesignerCart = createCartClient(window);
})();
